/* eslint-disable no-nested-ternary */
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ReactNode, useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import { Text } from '@/components/ui/catalyst/text';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export interface BaseDetailDrawerProps<T> {
  // Data and state
  data: T | null | undefined;
  isLoading?: boolean;
  error?: Error | null;
  open: boolean;
  onClose: () => void;

  // Edit mode
  isEditing?: boolean;
  onEdit?: (item: T) => void;
  onEditModeChange?: (isEditing: boolean) => void;

  // Actions
  onDelete?: (item: T) => void;
  onUpdate?: (item: T) => void;

  // Content rendering
  renderIcon?: (item: T) => ReactNode;
  renderTitle?: (item: T) => string;
  renderDescription?: (item: T) => string;
  renderContent?: (item: T) => ReactNode;
  renderEditForm?: (
    item: T,
    onSuccess: (updatedItem: T) => void,
    onCancel: () => void,
  ) => ReactNode;

  // Customization
  className?: string;
  contentClassName?: string;
  loadingMessage?: string;
  errorMessage?: string;
  editTitle?: string;
  editDescription?: string;

  // Action customization
  canEdit?: (item: T) => boolean;
  canDelete?: (item: T) => boolean;
  editButtonLabel?: string;
  deleteButtonLabel?: string;
}

/**
 * Base drawer component for displaying detailed views of PostGrid entities.
 * Handles common patterns like loading, error states, edit mode toggle, and actions.
 */
export const BaseDetailDrawer = <T,>({
  data,
  isLoading = false,
  error = null,
  open,
  onClose,
  isEditing: externalIsEditing,
  onEdit,
  onEditModeChange,
  onDelete,
  onUpdate,
  renderIcon,
  renderTitle,
  renderDescription,
  renderContent,
  renderEditForm,
  className,
  contentClassName,
  loadingMessage = 'Loading details...',
  errorMessage = 'Error loading details',
  editTitle = 'Edit Item',
  editDescription = 'Update item information and settings',
  canEdit = () => true,
  canDelete = () => true,
  editButtonLabel = 'Edit',
  deleteButtonLabel = 'Delete',
}: BaseDetailDrawerProps<T>) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);

  // Use external editing state if provided, otherwise use internal
  const isEditing =
    externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onEditModeChange || setInternalIsEditing;

  const handleEdit = () => {
    if (data && onEdit) {
      onEdit(data);
    } else {
      setIsEditing(true);
    }
  };

  const handleDelete = () => {
    if (data && onDelete) {
      onDelete(data);
    }
  };

  const handleFormSuccess = (updatedItem: T) => {
    setIsEditing(false);
    onUpdate?.(updatedItem);
  };

  const handleFormCancel = () => {
    setIsEditing(false);
  };

  if (!open) return null;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side="right"
        className={cn('w-full sm:w-[600px] sm:max-w-[90vw]', className)}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Text>{loadingMessage}</Text>
          </div>
        ) : error || !data ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <Text className="text-red-600">
              {error ? errorMessage : 'Item not found'}
            </Text>
            <Button plain onClick={onClose}>
              Close
            </Button>
          </div>
        ) : isEditing ? (
          // Edit Mode
          <div className="h-full flex flex-col">
            <SheetHeader className="flex-shrink-0 pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>{editTitle}</SheetTitle>
                  <SheetDescription>{editDescription}</SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {renderEditForm?.(data, handleFormSuccess, handleFormCancel)}
            </div>
          </div>
        ) : (
          // View Mode
          <div className="h-full flex flex-col">
            <SheetHeader className="flex-shrink-0 pb-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  {/* Icon */}
                  {renderIcon && (
                    <div className="flex-shrink-0 mt-1">{renderIcon(data)}</div>
                  )}

                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-xl">
                      {renderTitle?.(data)}
                    </SheetTitle>

                    {renderDescription && (
                      <SheetDescription>
                        {renderDescription(data)}
                      </SheetDescription>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {canEdit(data) && (
                    <Button plain onClick={handleEdit}>
                      <PencilIcon className="h-4 w-4" />
                      {editButtonLabel}
                    </Button>
                  )}

                  {onDelete && canDelete(data) && (
                    <Button
                      plain
                      onClick={handleDelete}
                      className="text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                      {deleteButtonLabel}
                    </Button>
                  )}
                </div>
              </div>
            </SheetHeader>

            <div
              className={cn(
                'flex-1 overflow-y-auto px-6 pb-6',
                contentClassName,
              )}
            >
              {renderContent?.(data)}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
